import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { ChevronLeft, FileText, Plus, X } from "lucide-react";

const METHODS = [
  { value: "cash", label: "Cash" },
  { value: "upi", label: "UPI" },
  { value: "bank", label: "Bank" },
  { value: "cheque", label: "Cheque" },
  { value: "other", label: "Other" },
];
const todayISO = () => new Date().toISOString().slice(0, 10);
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function BuyerLedger({ buyerId, onBack }) {
  const [ledger, setLedger] = useState(null);
  const [dispatches, setDispatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [form, setForm] = useState({ dispatch_id: "", amount: "", method: "upi", reference: "", notes: "", received_on: todayISO() });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [l, d] = await Promise.all([
        api.get(`/staff/buyers/${buyerId}/ledger`),
        api.get(`/staff/dispatches`, { params: { buyer_id: buyerId } }),
      ]);
      setLedger(l.data);
      setDispatches(d.data);
    } catch { toast.error("Could not load ledger"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [buyerId]);

  const eligibleDispatches = useMemo(
    () => dispatches.filter((d) => d.status === "sent"),
    [dispatches],
  );

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("tbb_token");
      const res = await fetch(`${API}/staff/buyers/${buyerId}/ledger.pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (ledger?.buyer?.name || "buyer").replace(/[^\w]+/g, "_");
      a.download = `TBB-Buyer-Ledger-${safe}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(err.message || "PDF failed"); }
    finally { setDownloading(false); }
  };

  const savePayment = async (e) => {
    e.preventDefault();
    if (!form.amount || Number(form.amount) <= 0) { toast.error("Amount must be > 0"); return; }
    setSaving(true);
    try {
      await api.post("/staff/buyer-payments", {
        buyer_id: buyerId,
        dispatch_id: form.dispatch_id || "",
        amount: Number(form.amount),
        method: form.method,
        reference: form.reference,
        notes: form.notes,
        received_on: form.received_on,
      });
      toast.success("Payment recorded");
      setPayOpen(false);
      setForm({ dispatch_id: "", amount: "", method: "upi", reference: "", notes: "", received_on: todayISO() });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (loading || !ledger) {
    return (
      <div data-testid="buyer-ledger" className="p-6">
        <button data-testid="buyer-ledger-back" onClick={onBack} className="inline-flex items-center gap-1 text-sm text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--ink))]">
          <ChevronLeft size={14} /> Back
        </button>
        <div className="mt-4 text-[hsl(var(--ink-soft))]">Loading…</div>
      </div>
    );
  }

  const buyer = ledger.buyer || {};
  const s = ledger.summary || {};
  const balanceClass = s.balance_due > 0 ? "text-[hsl(6_62%_45%)]" : (s.balance_due < 0 ? "text-[hsl(var(--forest))]" : "");

  return (
    <div data-testid="buyer-ledger">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <button data-testid="buyer-ledger-back" onClick={onBack} className="inline-flex items-center gap-1 text-sm text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--ink))]">
            <ChevronLeft size={14} /> Back to Product Out
          </button>
          <div className="mt-2 eyebrow">Buyer ledger</div>
          <h1 className="display text-3xl">{buyer.name}</h1>
          <div className="text-sm text-[hsl(var(--ink-soft))]">
            {buyer.category && <>{buyer.category} · </>}
            {buyer.phone && <>{buyer.phone} · </>}
            {buyer.email}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="buyer-ledger-pdf" onClick={downloadPdf} disabled={downloading} className="inline-flex items-center gap-2 px-3 py-2 border border-[hsl(var(--rule))] text-sm hover:bg-[hsl(var(--bg-2))] disabled:opacity-50">
            <FileText size={14} /> {downloading ? "…" : "PDF"}
          </button>
          <button data-testid="buyer-ledger-add-payment" onClick={() => setPayOpen(true)} className="btn-brass !py-2 !px-4 text-sm">
            <Plus size={14} /> Record payment
          </button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
        <SummaryTile testid="ledger-dispatched" label="Total dispatched" value={inr(s.total_dispatched)} />
        <SummaryTile testid="ledger-received" label="Total received" value={inr(s.total_received)} />
        <SummaryTile testid="ledger-balance" label={s.balance_due < 0 ? "Advance received" : "Balance due"} value={inr(Math.abs(s.balance_due))} highlight={balanceClass} />
      </div>

      <div className="mt-6 border border-[hsl(var(--rule))] bg-[hsl(var(--bg))] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--bg-2))] text-left text-xs uppercase tracking-wide text-[hsl(var(--ink-soft))] sticky top-0 z-10 shadow-[0_1px_0_hsl(var(--rule))]">
            <tr>
              <th className="px-4 py-2">Date</th>
              <th className="px-4 py-2">Type</th>
              <th className="px-4 py-2">Reference</th>
              <th className="px-4 py-2">Dispatch #</th>
              <th className="px-4 py-2 text-right">Debit</th>
              <th className="px-4 py-2 text-right">Credit</th>
              <th className="px-4 py-2 text-right">Balance</th>
            </tr>
          </thead>
          <tbody>
            {(ledger.transactions || []).length === 0 && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-[hsl(var(--ink-soft))]">No transactions yet.</td></tr>
            )}
            {(ledger.transactions || []).map((t, i) => (
              <tr key={i} data-testid={`ledger-txn-${i}`} className="border-t border-[hsl(var(--rule))]">
                <td className="px-4 py-2">{t.date}</td>
                <td className="px-4 py-2">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${t.type === "dispatch" ? "bg-[hsl(var(--brass)/0.15)] text-[hsl(var(--brass-2))]" : "bg-[hsl(var(--forest)/0.15)] text-[hsl(var(--forest))]"}`}>
                    {t.type}
                  </span>
                </td>
                <td className="px-4 py-2 text-xs">
                  {t.type === "payment" && t.method ? (
                    <><span className="uppercase">{t.method}</span>{t.ref ? <span className="text-[hsl(var(--ink-soft))]"> · {t.ref}</span> : null}</>
                  ) : (t.ref || "—")}
                </td>
                <td className="px-4 py-2 text-xs">{t.dispatch_number || "—"}</td>
                <td className="px-4 py-2 text-right">{t.debit ? inr(t.debit) : "—"}</td>
                <td className="px-4 py-2 text-right">{t.credit ? inr(t.credit) : "—"}</td>
                <td className="px-4 py-2 text-right font-semibold">{inr(t.balance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {payOpen && (
        <div data-testid="payment-modal" className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) setPayOpen(false); }}>
          <form onSubmit={savePayment} className="bg-[hsl(var(--bg))] border border-[hsl(var(--rule))] w-full max-w-md p-6 shadow-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="eyebrow">Record payment</div>
                <h2 className="mt-1 text-xl display">{buyer.name}</h2>
              </div>
              <button type="button" onClick={() => setPayOpen(false)} className="text-[hsl(var(--ink-soft))]"><X size={18} /></button>
            </div>

            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="col-span-2 text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Against dispatch (optional)
                <select data-testid="payment-dispatch" value={form.dispatch_id} onChange={(e) => setForm({ ...form, dispatch_id: e.target.value })} className="tbb-inp mt-1 w-full">
                  <option value="">— General payment —</option>
                  {eligibleDispatches.map((d) => (
                    <option key={d.id} value={d.id}>{d.dispatch_number} · {d.dispatch_date} · {inr(d.subtotal)}</option>
                  ))}
                </select>
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Amount *
                <input required data-testid="payment-amount" type="number" min="0.01" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="tbb-inp mt-1 w-full" />
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Method
                <select data-testid="payment-method" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} className="tbb-inp mt-1 w-full">
                  {METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Received on *
                <input required type="date" data-testid="payment-date" value={form.received_on} onChange={(e) => setForm({ ...form, received_on: e.target.value })} className="tbb-inp mt-1 w-full" />
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Reference
                <input data-testid="payment-reference" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="tbb-inp mt-1 w-full" placeholder="UPI txn / Cheque no." />
              </label>
              <label className="col-span-2 text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Notes
                <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="tbb-inp mt-1 w-full" />
              </label>
            </div>

            <div className="mt-5 flex justify-end gap-3">
              <button type="button" onClick={() => setPayOpen(false)} className="text-sm text-[hsl(var(--ink-soft))] px-3">Cancel</button>
              <button type="submit" data-testid="payment-save" disabled={saving} className="btn-brass !py-2.5 !px-5 text-sm">{saving ? "Saving…" : "Save payment"}</button>
            </div>
            <style>{`.tbb-inp{background:hsl(var(--bg));border:1px solid hsl(var(--rule));padding:0.55rem 0.75rem;font-size:0.9rem}.tbb-inp:focus{outline:1px solid hsl(var(--forest));border-color:hsl(var(--forest))}`}</style>
          </form>
        </div>
      )}
    </div>
  );
}

function SummaryTile({ label, value, testid, highlight }) {
  return (
    <div data-testid={testid} className="border border-[hsl(var(--rule))] bg-[hsl(var(--bg))] p-4">
      <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))]">{label}</div>
      <div className={`display text-2xl mt-1 ${highlight || ""}`}>{value}</div>
    </div>
  );
}
